import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, X, Save, Settings as SettingsIcon, Tag, Package, Receipt, Trash2, CreditCard, Percent, TreePine, Edit2, Mail, Clock, Send, Check, Shield, Eye, EyeOff, Lock, Timer, Bell, BellOff, Smartphone } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { isPushSupported, getExistingSubscription, subscribeToPush, unsubscribeFromPush } from '@/utils/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Função para formatar moeda brasileira
const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return '';
  return valor.toLocaleString('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Função para formatar input de moeda
const formatarInputMoeda = (valor) => {
  let numeros = valor.replace(/\D/g, '');
  if (!numeros) return '';
  let valorNumerico = parseInt(numeros, 10);
  let valorDecimal = valorNumerico / 100;
  return valorDecimal.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Função para converter moeda formatada em número
const parseMoeda = (valorStr) => {
  if (!valorStr) return 0;
  const limpo = valorStr.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(limpo) || 0;
};

export default function Settings() {
  const [categories, setCategories] = useState({
    bill: [],
    product: [],
    transaction: []
  });
  const [cardRates, setCardRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({ bill: '', product: '', transaction: '' });
  const [hasChanges, setHasChanges] = useState({ bill: false, product: false, transaction: false });
  const [hasRatesChanges, setHasRatesChanges] = useState(false);
  
  // Estados para Tipos de Madeira
  const [tiposMadeira, setTiposMadeira] = useState([]);
  const [novoTipo, setNovoTipo] = useState({ nome: '', preco_m3: '', preco_m3_display: '' });
  const [editandoTipo, setEditandoTipo] = useState(null);
  const [loadingMadeira, setLoadingMadeira] = useState(false);

  // Estados para Notificações por Email
  const [emailConfig, setEmailConfig] = useState({
    emails_destinatarios: [],
    horario_matinal: '07:30',
    horario_vespertino: '16:50',
    ativo: true
  });
  const [novoEmail, setNovoEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [savingEmailConfig, setSavingEmailConfig] = useState(false);

  // Estados para Notificações Push (PWA)
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushConfig, setPushConfig] = useState({
    ativo: true,
    horario_manha: '08:00',
    horario_resumo: '18:00',
    eventos: {
      pedido_criado: true,
      pagamento_registrado: true,
      conta_paga: true,
      pedido_entregue: true,
      contas_vencem_hoje: true,
      contas_a_vencer: true,
      cheques_compensar: true,
      parcelas_atrasadas: true,
      resumo_vendas: true,
    },
  });
  const [savingPushConfig, setSavingPushConfig] = useState(false);

  // Estados para Segurança
  const [securityConfig, setSecurityConfig] = useState({
    bloqueio_inatividade_ativo: false,
    tempo_inatividade_minutos: 5,
    bloqueio_financeiro_ativo: false,
    tem_senha_desbloqueio: false,
    tem_senha_financeiro: false
  });
  const [senhaDesbloqueio, setSenhaDesbloqueio] = useState('');
  const [confirmarSenhaDesbloqueio, setConfirmarSenhaDesbloqueio] = useState('');
  const [senhaFinanceiro, setSenhaFinanceiro] = useState('');
  const [confirmarSenhaFinanceiro, setConfirmarSenhaFinanceiro] = useState('');
  const [showSenhaDesbloqueio, setShowSenhaDesbloqueio] = useState(false);
  const [showSenhaFinanceiro, setShowSenhaFinanceiro] = useState(false);
  const [savingSecurity, setSavingSecurity] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchCardRates();
    fetchTiposMadeira();
    fetchEmailConfig();
    fetchSecurityConfig();
    fetchPushConfig();
    initPushStatus();
  }, []);

  const initPushStatus = async () => {
    const supported = isPushSupported();
    setPushSupported(supported);
    if (supported) {
      try {
        const sub = await getExistingSubscription();
        setPushSubscribed(!!sub);
      } catch (e) {
        setPushSubscribed(false);
      }
    }
  };

  const fetchPushConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/push-notifications`);
      setPushConfig((prev) => ({
        ...prev,
        ...res.data,
        eventos: { ...prev.eventos, ...(res.data.eventos || {}) },
      }));
    } catch (e) {
      // usa defaults
    }
  };

  const handleActivatePush = async () => {
    setPushLoading(true);
    try {
      await subscribeToPush();
      setPushSubscribed(true);
      toast.success('Notificações ativadas neste aparelho!');
    } catch (e) {
      toast.error(e.message || 'Erro ao ativar notificações');
    } finally {
      setPushLoading(false);
    }
  };

  const handleDeactivatePush = async () => {
    setPushLoading(true);
    try {
      await unsubscribeFromPush();
      setPushSubscribed(false);
      toast.success('Notificações desativadas neste aparelho');
    } catch (e) {
      toast.error('Erro ao desativar notificações');
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    try {
      await axios.post(`${API}/push/test`);
      toast.success('Notificação de teste enviada! Confira seu aparelho.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar notificação de teste');
    }
  };

  const handleSavePushConfig = async () => {
    setSavingPushConfig(true);
    try {
      await axios.put(`${API}/settings/push-notifications`, pushConfig);
      toast.success('Configurações de notificações salvas!');
    } catch (e) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingPushConfig(false);
    }
  };

  const togglePushEvento = (key) => {
    setPushConfig((prev) => ({
      ...prev,
      eventos: { ...prev.eventos, [key]: !prev.eventos[key] },
    }));
  };

  const fetchEmailConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/email-notifications`);
      setEmailConfig(res.data);
    } catch (error) {
      console.error('Erro ao carregar config de email:', error);
    }
  };

  const fetchSecurityConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/settings/security`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecurityConfig(res.data);
    } catch (error) {
      console.error('Erro ao carregar config de segurança:', error);
    }
  };

  const handleSaveSecurityConfig = async () => {
    // Validar senhas de desbloqueio
    if (securityConfig.bloqueio_inatividade_ativo && senhaDesbloqueio) {
      if (senhaDesbloqueio !== confirmarSenhaDesbloqueio) {
        toast.error('As senhas de desbloqueio não coincidem');
        return;
      }
      if (senhaDesbloqueio.length < 4) {
        toast.error('A senha de desbloqueio deve ter pelo menos 4 caracteres');
        return;
      }
    }

    // Validar senhas do financeiro
    if (securityConfig.bloqueio_financeiro_ativo && senhaFinanceiro) {
      if (senhaFinanceiro !== confirmarSenhaFinanceiro) {
        toast.error('As senhas do financeiro não coincidem');
        return;
      }
      if (senhaFinanceiro.length < 4) {
        toast.error('A senha do financeiro deve ter pelo menos 4 caracteres');
        return;
      }
    }

    setSavingSecurity(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        bloqueio_inatividade_ativo: securityConfig.bloqueio_inatividade_ativo,
        tempo_inatividade_minutos: securityConfig.tempo_inatividade_minutos,
        bloqueio_financeiro_ativo: securityConfig.bloqueio_financeiro_ativo
      };

      // Só envia as senhas se foram preenchidas
      if (senhaDesbloqueio) {
        payload.senha_desbloqueio = senhaDesbloqueio;
      }
      if (senhaFinanceiro) {
        payload.senha_financeiro = senhaFinanceiro;
      }

      const res = await axios.put(`${API}/settings/security`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSecurityConfig(res.data);
      setSenhaDesbloqueio('');
      setConfirmarSenhaDesbloqueio('');
      setSenhaFinanceiro('');
      setConfirmarSenhaFinanceiro('');
      toast.success('Configurações de segurança salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações de segurança');
    } finally {
      setSavingSecurity(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    setSavingEmailConfig(true);
    try {
      await axios.put(`${API}/settings/email-notifications`, emailConfig);
      toast.success('Configurações de email salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingEmailConfig(false);
    }
  };

  const handleAddEmail = () => {
    if (!novoEmail.trim()) return;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(novoEmail.trim())) {
      toast.error('Email inválido');
      return;
    }
    if (emailConfig.emails_destinatarios.includes(novoEmail.trim())) {
      toast.error('Email já adicionado');
      return;
    }
    setEmailConfig({
      ...emailConfig,
      emails_destinatarios: [...emailConfig.emails_destinatarios, novoEmail.trim()]
    });
    setNovoEmail('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmailConfig({
      ...emailConfig,
      emails_destinatarios: emailConfig.emails_destinatarios.filter(e => e !== emailToRemove)
    });
  };

  const handleTestEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/notifications/test-email`);
      toast.success('Email de teste enviado! Verifique sua caixa de entrada.');
    } catch (error) {
      toast.error('Erro ao enviar email de teste');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendMorningEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/notifications/send-morning-email`);
      toast.success('Resumo financeiro enviado!');
    } catch (error) {
      toast.error('Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendAfternoonEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/notifications/send-afternoon-email`);
      toast.success('Email de pendências enviado!');
    } catch (error) {
      toast.error('Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/settings/all-categories`);
      setCategories(res.data);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const fetchCardRates = async () => {
    try {
      const res = await axios.get(`${API}/settings/card-rates`);
      setCardRates(res.data);
    } catch (error) {
      console.error('Erro ao carregar taxas de cartão:', error);
    }
  };

  const fetchTiposMadeira = async () => {
    try {
      const res = await axios.get(`${API}/tipos-madeira`);
      setTiposMadeira(res.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de madeira:', error);
    }
  };

  const handlePrecoChange = (e) => {
    const formatted = formatarInputMoeda(e.target.value);
    const valorNumerico = parseMoeda(formatted);
    setNovoTipo(prev => ({ 
      ...prev, 
      preco_m3: valorNumerico,
      preco_m3_display: formatted
    }));
  };

  const handleAddTipoMadeira = async () => {
    if (!novoTipo.nome.trim()) {
      toast.error('Informe o nome da madeira');
      return;
    }
    if (!novoTipo.preco_m3) {
      toast.error('Informe o preço por m³');
      return;
    }

    setLoadingMadeira(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tipos-madeira`, {
        nome: novoTipo.nome,
        preco_m3: novoTipo.preco_m3
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tipo de madeira cadastrado!');
      setNovoTipo({ nome: '', preco_m3: '', preco_m3_display: '' });
      fetchTiposMadeira();
    } catch (error) {
      toast.error('Erro ao cadastrar tipo de madeira');
    } finally {
      setLoadingMadeira(false);
    }
  };

  const handleUpdateTipoMadeira = async () => {
    if (!editandoTipo) return;

    setLoadingMadeira(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tipos-madeira/${editandoTipo.id}`, {
        nome: editandoTipo.nome,
        preco_m3: editandoTipo.preco_m3
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tipo de madeira atualizado!');
      setEditandoTipo(null);
      fetchTiposMadeira();
    } catch (error) {
      toast.error('Erro ao atualizar tipo de madeira');
    } finally {
      setLoadingMadeira(false);
    }
  };

  const handleDeleteTipoMadeira = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este tipo de madeira?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tipos-madeira/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tipo de madeira removido!');
      fetchTiposMadeira();
    } catch (error) {
      toast.error('Erro ao remover tipo de madeira');
    }
  };

  const handleEditPrecoChange = (e) => {
    const formatted = formatarInputMoeda(e.target.value);
    const valorNumerico = parseMoeda(formatted);
    setEditandoTipo(prev => ({ 
      ...prev, 
      preco_m3: valorNumerico,
      preco_m3_display: formatted
    }));
  };

  const handleRateChange = (tipo, bandeira, parcela, value) => {
    setCardRates(prev => {
      const newRates = JSON.parse(JSON.stringify(prev));
      if (parcela) {
        newRates[tipo][bandeira][parcela] = parseFloat(value) || 0;
      } else {
        newRates[tipo][bandeira] = parseFloat(value) || 0;
      }
      return newRates;
    });
    setHasRatesChanges(true);
  };

  const handleSaveRates = async () => {
    try {
      await axios.put(`${API}/settings/card-rates`, cardRates);
      toast.success('Taxas de cartão salvas com sucesso!');
      setHasRatesChanges(false);
    } catch (error) {
      toast.error('Erro ao salvar taxas');
    }
  };

  const handleAddCategory = (type) => {
    const value = newCategory[type].trim();
    if (!value) return;
    if (categories[type].includes(value)) {
      toast.error('Categoria já existe');
      return;
    }
    setCategories(prev => ({
      ...prev,
      [type]: [...prev[type], value]
    }));
    setNewCategory(prev => ({ ...prev, [type]: '' }));
    setHasChanges(prev => ({ ...prev, [type]: true }));
  };

  const handleRemoveCategory = (type, category) => {
    setCategories(prev => ({
      ...prev,
      [type]: prev[type].filter(c => c !== category)
    }));
    setHasChanges(prev => ({ ...prev, [type]: true }));
  };

  const handleSave = async (type) => {
    try {
      await axios.put(`${API}/settings/categories/${type}`, {
        categories: categories[type]
      });
      toast.success('Categorias salvas com sucesso!');
      setHasChanges(prev => ({ ...prev, [type]: false }));
    } catch (error) {
      toast.error('Erro ao salvar categorias');
    }
  };

  const categoryConfig = [
    {
      type: 'bill',
      title: 'Categorias de Contas a Pagar',
      description: 'Categorias usadas nas despesas e contas a pagar',
      icon: Receipt,
      color: 'text-red-500'
    },
    {
      type: 'product',
      title: 'Categorias de Produtos',
      description: 'Categorias para organizar seus produtos',
      icon: Package,
      color: 'text-blue-500'
    },
    {
      type: 'transaction',
      title: 'Categorias de Transações',
      description: 'Categorias para classificar receitas e despesas',
      icon: Tag,
      color: 'text-green-500'
    }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold font-manrope flex items-center gap-3">
            <SettingsIcon className="h-8 w-8" />
            Configurações
          </h1>
          <p className="text-muted-foreground mt-1">Gerencie as categorias e configurações do sistema</p>
        </div>

        <Tabs defaultValue="categories" className="w-full">
          <TabsList className="mb-4 flex-wrap h-auto gap-1">
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Categorias
            </TabsTrigger>
            <TabsTrigger value="madeiras" className="flex items-center gap-2">
              <TreePine className="h-4 w-4" />
              Madeiras
            </TabsTrigger>
            <TabsTrigger value="card-rates" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Taxas de Cartão
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Notificações
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2" data-testid="security-tab">
              <Shield className="h-4 w-4" />
              Segurança
            </TabsTrigger>
          </TabsList>

          <TabsContent value="categories" className="space-y-6">
            {categoryConfig.map(({ type, title, description, icon: Icon, color }) => (
              <Card key={type} data-testid={`category-card-${type}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${color}`} />
                    {title}
                  </CardTitle>
                  <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {categories[type].map(category => (
                      <Badge 
                        key={category} 
                        variant="secondary" 
                        className="px-3 py-1.5 text-sm flex items-center gap-2"
                      >
                        {category}
                        <button
                          onClick={() => handleRemoveCategory(type, category)}
                          className="hover:text-destructive transition-colors"
                          title="Remover categoria"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {categories[type].length === 0 && (
                      <p className="text-muted-foreground text-sm">Nenhuma categoria cadastrada</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Input
                      placeholder="Nova categoria..."
                      value={newCategory[type]}
                      onChange={(e) => setNewCategory(prev => ({ ...prev, [type]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddCategory(type)}
                      className="max-w-xs"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleAddCategory(type)}
                      title="Adicionar categoria"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    {hasChanges[type] && (
                      <Button onClick={() => handleSave(type)}>
                        <Save className="h-4 w-4 mr-2" />
                        Salvar Alterações
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="madeiras" className="space-y-6">
            <Card data-testid="tipos-madeira-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TreePine className="h-5 w-5 text-amber-600" />
                  Tipos de Madeira
                </CardTitle>
                <CardDescription>
                  Cadastre os tipos de madeira com seus preços padrão por m³. 
                  Esses valores serão usados na calculadora de Romaneio.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Formulário para adicionar novo tipo */}
                <div className="flex flex-col sm:flex-row gap-3 p-4 bg-muted/30 rounded-lg">
                  <div className="flex-1">
                    <Input
                      placeholder="Nome da madeira (ex: Ipê, Cedrinho, Jatobá)"
                      value={novoTipo.nome}
                      onChange={(e) => setNovoTipo(prev => ({ ...prev, nome: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTipoMadeira()}
                    />
                  </div>
                  <div className="w-full sm:w-48">
                    <Input
                      placeholder="R$ 0,00"
                      value={novoTipo.preco_m3_display}
                      onChange={handlePrecoChange}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTipoMadeira()}
                    />
                  </div>
                  <Button onClick={handleAddTipoMadeira} disabled={loadingMadeira}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar
                  </Button>
                </div>

                {/* Lista de tipos cadastrados */}
                {tiposMadeira.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    Nenhum tipo de madeira cadastrado ainda.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {tiposMadeira.map(tipo => (
                      <div 
                        key={tipo.id} 
                        className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border"
                      >
                        {editandoTipo?.id === tipo.id ? (
                          // Modo edição
                          <div className="flex flex-1 gap-3 items-center">
                            <Input
                              value={editandoTipo.nome}
                              onChange={(e) => setEditandoTipo(prev => ({ ...prev, nome: e.target.value }))}
                              className="flex-1"
                            />
                            <Input
                              value={editandoTipo.preco_m3_display}
                              onChange={handleEditPrecoChange}
                              className="w-40"
                            />
                            <Button size="sm" onClick={handleUpdateTipoMadeira} disabled={loadingMadeira}>
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditandoTipo(null)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          // Modo visualização
                          <>
                            <div className="flex items-center gap-3">
                              <TreePine className="h-5 w-5 text-amber-600" />
                              <span className="font-medium">{tipo.nome}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="secondary" className="text-sm">
                                {formatarMoeda(tipo.preco_m3)}/m³
                              </Badge>
                              <Button 
                                size="icon" 
                                variant="ghost"
                                onClick={() => setEditandoTipo({
                                  ...tipo,
                                  preco_m3_display: formatarMoeda(tipo.preco_m3)
                                })}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteTipoMadeira(tipo.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="card-rates" className="space-y-6">
            {cardRates && (
              <>
                {/* Taxas de Débito */}
                <Card data-testid="card-rates-debito">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-blue-500" />
                      Taxas de Débito
                    </CardTitle>
                    <CardDescription>Configure as taxas para transações de débito por bandeira</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Mastercard/Visa</label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            value={cardRates.debito?.mastercard_visa || 0}
                            onChange={(e) => handleRateChange('debito', 'mastercard_visa', null, e.target.value)}
                            className="pr-8"
                          />
                          <Percent className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Elo</label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            value={cardRates.debito?.elo || 0}
                            onChange={(e) => handleRateChange('debito', 'elo', null, e.target.value)}
                            className="pr-8"
                          />
                          <Percent className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Amex</label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            value={cardRates.debito?.amex || 0}
                            onChange={(e) => handleRateChange('debito', 'amex', null, e.target.value)}
                            className="pr-8"
                          />
                          <Percent className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Taxas de Crédito - Mastercard/Visa */}
                <Card data-testid="card-rates-credito-mv">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-purple-500" />
                      Taxas de Crédito - Mastercard/Visa
                    </CardTitle>
                    <CardDescription>Configure as taxas por número de parcelas</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(parcela => (
                        <div key={parcela} className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">{parcela}x</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={cardRates.credito?.mastercard_visa?.[String(parcela)] || 0}
                              onChange={(e) => handleRateChange('credito', 'mastercard_visa', String(parcela), e.target.value)}
                              className="pr-6 text-sm h-9"
                            />
                            <span className="absolute right-2 top-2 text-xs text-muted-foreground">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Taxas de Crédito - Elo/Amex */}
                <Card data-testid="card-rates-credito-ea">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-orange-500" />
                      Taxas de Crédito - Elo/Amex
                    </CardTitle>
                    <CardDescription>Configure as taxas por número de parcelas</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(parcela => (
                        <div key={parcela} className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">{parcela}x</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={cardRates.credito?.elo_amex?.[String(parcela)] || 0}
                              onChange={(e) => handleRateChange('credito', 'elo_amex', String(parcela), e.target.value)}
                              className="pr-6 text-sm h-9"
                            />
                            <span className="absolute right-2 top-2 text-xs text-muted-foreground">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {hasRatesChanges && (
                  <div className="flex justify-end">
                    <Button onClick={handleSaveRates} size="lg">
                      <Save className="h-4 w-4 mr-2" />
                      Salvar Taxas de Cartão
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Aba de Notificações por Email */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-500" />
                  Notificações por Email
                </CardTitle>
                <CardDescription>
                  Configure e gerencie os avisos automáticos por email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Emails Destinatários */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Emails Destinatários
                  </h4>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Digite um email e pressione Adicionar"
                      value={novoEmail}
                      onChange={(e) => setNovoEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                    />
                    <Button onClick={handleAddEmail} variant="outline">
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {emailConfig.emails_destinatarios?.map((email, idx) => (
                      <Badge key={idx} variant="secondary" className="px-3 py-1 text-sm">
                        {email}
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="ml-2 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!emailConfig.emails_destinatarios || emailConfig.emails_destinatarios.length === 0) && (
                      <p className="text-sm text-muted-foreground">Nenhum email configurado</p>
                    )}
                  </div>
                </div>

                {/* Horários Programados */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Horários de Envio (Brasília)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        ☀️ Email Matinal (Resumo do Dia)
                      </label>
                      <Input
                        type="time"
                        value={emailConfig.horario_matinal}
                        onChange={(e) => setEmailConfig({ ...emailConfig, horario_matinal: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Contas a pagar, cheques, recebimentos de cartão
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        ⚠️ Email Vespertino (Pendências)
                      </label>
                      <Input
                        type="time"
                        value={emailConfig.horario_vespertino}
                        onChange={(e) => setEmailConfig({ ...emailConfig, horario_vespertino: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Alerta sobre contas e cheques não quitados
                      </p>
                    </div>
                  </div>
                </div>

                {/* Ativar/Desativar e Salvar */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="emailAtivo"
                      checked={emailConfig.ativo}
                      onChange={(e) => setEmailConfig({ ...emailConfig, ativo: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <label htmlFor="emailAtivo" className="text-sm font-medium">
                      Envio automático de emails ativo
                    </label>
                  </div>
                  <Button onClick={handleSaveEmailConfig} disabled={savingEmailConfig}>
                    <Save className="h-4 w-4 mr-2" />
                    {savingEmailConfig ? 'Salvando...' : 'Salvar Configurações'}
                  </Button>
                </div>

                {/* Ações Manuais */}
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Enviar Manualmente
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Use os botões abaixo para enviar os emails manualmente (para teste ou necessidade)
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      onClick={handleTestEmail}
                      disabled={sendingEmail}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      {sendingEmail ? 'Enviando...' : 'Testar Conexão'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleSendMorningEmail}
                      disabled={sendingEmail}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      ☀️ Enviar Resumo do Dia
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleSendAfternoonEmail}
                      disabled={sendingEmail}
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      ⚠️ Enviar Pendências
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ===== Card de Notificações Push (PWA) ===== */}
            <Card data-testid="push-notifications-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-purple-500" />
                  Notificações Push (PWA)
                </CardTitle>
                <CardDescription>
                  Receba alertas em tempo real no celular/PWA. Ative uma vez por aparelho.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Ativar neste aparelho */}
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    Este aparelho
                  </h4>
                  {!pushSupported ? (
                    <p className="text-sm text-amber-600">
                      Seu navegador não suporta notificações push. No iPhone, adicione o app à tela de início e abra pelo ícone.
                    </p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      {pushSubscribed ? (
                        <>
                          <Badge className="bg-green-500 hover:bg-green-500">
                            <Check className="h-3 w-3 mr-1" /> Ativado neste aparelho
                          </Badge>
                          <Button
                            variant="outline"
                            onClick={handleDeactivatePush}
                            disabled={pushLoading}
                            data-testid="deactivate-push-btn"
                          >
                            <BellOff className="h-4 w-4 mr-2" />
                            Desativar
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleTestPush}
                            data-testid="test-push-btn"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Enviar Teste
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={handleActivatePush}
                          disabled={pushLoading}
                          data-testid="activate-push-btn"
                        >
                          <Bell className="h-4 w-4 mr-2" />
                          {pushLoading ? 'Ativando...' : 'Ativar Notificações'}
                        </Button>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Instale o site na tela de início (PWA) para receber notificações mesmo com o navegador fechado.
                  </p>
                </div>

                {/* Eventos */}
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <SettingsIcon className="h-4 w-4" />
                    Eventos que geram notificação
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { key: 'pedido_criado', label: '🧾 Novo pedido criado' },
                      { key: 'pagamento_registrado', label: '💰 Pagamento registrado' },
                      { key: 'conta_paga', label: '✅ Conta paga' },
                      { key: 'pedido_entregue', label: '📦 Pedido entregue' },
                      { key: 'contas_vencem_hoje', label: '🔔 Contas vencem hoje (matinal)' },
                      { key: 'contas_a_vencer', label: '⚠️ Contas vencem amanhã (matinal)' },
                      { key: 'cheques_compensar', label: '🏦 Cheques a compensar (matinal)' },
                      { key: 'parcelas_atrasadas', label: '🔴 Parcelas de clientes atrasadas (matinal)' },
                      { key: 'resumo_vendas', label: '📊 Resumo diário de vendas' },
                    ].map((evt) => (
                      <div key={evt.key} className="flex items-center justify-between p-2 rounded bg-muted/40">
                        <label htmlFor={`push-${evt.key}`} className="text-sm">{evt.label}</label>
                        <Switch
                          id={`push-${evt.key}`}
                          checked={!!pushConfig.eventos?.[evt.key]}
                          onCheckedChange={() => togglePushEvento(evt.key)}
                          data-testid={`push-evt-${evt.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Horários */}
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Horários dos resumos (Brasília)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">☀️ Resumo Matinal</label>
                      <Input
                        type="time"
                        value={pushConfig.horario_manha}
                        onChange={(e) => setPushConfig({ ...pushConfig, horario_manha: e.target.value })}
                        data-testid="push-horario-manha"
                      />
                      <p className="text-xs text-muted-foreground">
                        Contas a vencer, cheques e parcelas atrasadas.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">🌙 Resumo Vespertino</label>
                      <Input
                        type="time"
                        value={pushConfig.horario_resumo}
                        onChange={(e) => setPushConfig({ ...pushConfig, horario_resumo: e.target.value })}
                        data-testid="push-horario-resumo"
                      />
                      <p className="text-xs text-muted-foreground">
                        Resumo diário de vendas.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Ativar/Desativar globalmente e salvar */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="pushAtivo"
                      checked={pushConfig.ativo}
                      onCheckedChange={(v) => setPushConfig({ ...pushConfig, ativo: v })}
                      data-testid="push-ativo-switch"
                    />
                    <label htmlFor="pushAtivo" className="text-sm font-medium">
                      Envio automático de notificações push ativo
                    </label>
                  </div>
                  <Button onClick={handleSavePushConfig} disabled={savingPushConfig} data-testid="save-push-config-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {savingPushConfig ? 'Salvando...' : 'Salvar Configurações'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba de Segurança */}
          <TabsContent value="security" className="space-y-6" data-testid="security-tab-content">
            {/* Bloqueio por Inatividade */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-amber-500" />
                  Bloqueio por Inatividade
                </CardTitle>
                <CardDescription>
                  Bloqueia a tela automaticamente após um período sem atividade (mouse, teclado ou toque).
                  Requer senha para desbloquear.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Lock className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Ativar bloqueio por inatividade</p>
                      <p className="text-sm text-muted-foreground">
                        A tela será bloqueada automaticamente após o tempo definido
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={securityConfig.bloqueio_inatividade_ativo}
                    onCheckedChange={(checked) => setSecurityConfig({ ...securityConfig, bloqueio_inatividade_ativo: checked })}
                    data-testid="toggle-bloqueio-inatividade"
                  />
                </div>

                {securityConfig.bloqueio_inatividade_ativo && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="tempo-inatividade">Tempo de inatividade (minutos)</Label>
                      <Input
                        id="tempo-inatividade"
                        type="number"
                        min={1}
                        max={60}
                        value={securityConfig.tempo_inatividade_minutos}
                        onChange={(e) => setSecurityConfig({ 
                          ...securityConfig, 
                          tempo_inatividade_minutos: Math.max(1, Math.min(60, parseInt(e.target.value) || 5))
                        })}
                        className="max-w-32"
                        data-testid="input-tempo-inatividade"
                      />
                      <p className="text-xs text-muted-foreground">Entre 1 e 60 minutos</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="senha-desbloqueio">
                        {securityConfig.tem_senha_desbloqueio ? 'Nova senha de desbloqueio (deixe em branco para manter)' : 'Senha de desbloqueio'}
                      </Label>
                      <div className="relative max-w-sm">
                        <Input
                          id="senha-desbloqueio"
                          type={showSenhaDesbloqueio ? 'text' : 'password'}
                          placeholder="Digite a senha"
                          value={senhaDesbloqueio}
                          onChange={(e) => setSenhaDesbloqueio(e.target.value)}
                          className="pr-10"
                          data-testid="input-senha-desbloqueio"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowSenhaDesbloqueio(!showSenhaDesbloqueio)}
                        >
                          {showSenhaDesbloqueio ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {senhaDesbloqueio && (
                      <div className="space-y-2">
                        <Label htmlFor="confirmar-senha-desbloqueio">Confirmar senha</Label>
                        <div className="relative max-w-sm">
                          <Input
                            id="confirmar-senha-desbloqueio"
                            type={showSenhaDesbloqueio ? 'text' : 'password'}
                            placeholder="Confirme a senha"
                            value={confirmarSenhaDesbloqueio}
                            onChange={(e) => setConfirmarSenhaDesbloqueio(e.target.value)}
                            className="pr-10"
                            data-testid="input-confirmar-senha-desbloqueio"
                          />
                        </div>
                        {senhaDesbloqueio && confirmarSenhaDesbloqueio && senhaDesbloqueio !== confirmarSenhaDesbloqueio && (
                          <p className="text-xs text-destructive">As senhas não coincidem</p>
                        )}
                      </div>
                    )}

                    {securityConfig.tem_senha_desbloqueio && (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <Check className="h-3 w-3 mr-1" />
                        Senha configurada
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bloqueio do Financeiro */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  Proteção do Módulo Financeiro
                </CardTitle>
                <CardDescription>
                  Exige uma senha separada para acessar a aba Financeiro. 
                  A senha é solicitada a cada 30 minutos de uso.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Lock className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Ativar proteção do financeiro</p>
                      <p className="text-sm text-muted-foreground">
                        Solicita senha ao acessar o módulo financeiro
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={securityConfig.bloqueio_financeiro_ativo}
                    onCheckedChange={(checked) => setSecurityConfig({ ...securityConfig, bloqueio_financeiro_ativo: checked })}
                    data-testid="toggle-bloqueio-financeiro"
                  />
                </div>

                {securityConfig.bloqueio_financeiro_ativo && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="senha-financeiro">
                        {securityConfig.tem_senha_financeiro ? 'Nova senha do financeiro (deixe em branco para manter)' : 'Senha do financeiro'}
                      </Label>
                      <div className="relative max-w-sm">
                        <Input
                          id="senha-financeiro"
                          type={showSenhaFinanceiro ? 'text' : 'password'}
                          placeholder="Digite a senha"
                          value={senhaFinanceiro}
                          onChange={(e) => setSenhaFinanceiro(e.target.value)}
                          className="pr-10"
                          data-testid="input-senha-financeiro"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowSenhaFinanceiro(!showSenhaFinanceiro)}
                        >
                          {showSenhaFinanceiro ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {senhaFinanceiro && (
                      <div className="space-y-2">
                        <Label htmlFor="confirmar-senha-financeiro">Confirmar senha</Label>
                        <div className="relative max-w-sm">
                          <Input
                            id="confirmar-senha-financeiro"
                            type={showSenhaFinanceiro ? 'text' : 'password'}
                            placeholder="Confirme a senha"
                            value={confirmarSenhaFinanceiro}
                            onChange={(e) => setConfirmarSenhaFinanceiro(e.target.value)}
                            className="pr-10"
                            data-testid="input-confirmar-senha-financeiro"
                          />
                        </div>
                        {senhaFinanceiro && confirmarSenhaFinanceiro && senhaFinanceiro !== confirmarSenhaFinanceiro && (
                          <p className="text-xs text-destructive">As senhas não coincidem</p>
                        )}
                      </div>
                    )}

                    {securityConfig.tem_senha_financeiro && (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <Check className="h-3 w-3 mr-1" />
                        Senha configurada
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Botão Salvar */}
            <div className="flex justify-end">
              <Button onClick={handleSaveSecurityConfig} disabled={savingSecurity} size="lg" data-testid="save-security-btn">
                <Save className="h-4 w-4 mr-2" />
                {savingSecurity ? 'Salvando...' : 'Salvar Configurações de Segurança'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
